import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { isAdmin } from "auth/tier";
import { todayLocal } from "../../whiteboard/whiteboardTracking";
import {
  UPCOMING_WINDOWS,
  contractBody,
  contractFormProblem,
  emptyContractForm,
  formFromContract,
  isUpcoming,
  type ContractForm,
  type ContractRecord,
} from "./contracts.types";

/** Every contract HQ reads for the page; more is said, not hidden. */
export const CONTRACTS_LIMIT = 500;

/** The scope that lists every client's contracts. */
export const ALL_CONTRACTS = "all";

type RawService = {
  service: (path: string) => {
    create: (data: object) => Promise<unknown>;
    patch: (id: string, data: object) => Promise<unknown>;
    remove: (id: string) => Promise<unknown>;
  };
};

/**
 * Contracts and renewals (Blake's contracts tracker in HQ, #3075 / #2663).
 *
 * ONE class, two registrations: `contractsViewStore` (Clients → Contracts &
 * renewals, every client) and `clientContractsViewStore` (Client 360 →
 * Contracts, pinned to the open client by the ClientDetail route).
 *
 * The rows live in the `client-contracts` ServiceStore. Values come only to
 * administrators and finance roles — the API takes them off for anyone else,
 * so this store never has them to leak. Writes go through the RAW Feathers
 * service: a refusal (role, the client write gate) reaches the form in the
 * API's words, and nothing is cached from the answer.
 */
export default class ContractsStore {
  rootStore: RootStore;

  scope: string = ALL_CONTRACTS;
  loading = false;
  error: string | null = null;
  /** The page's window: renewals (or ends, or notice days) this many days ahead. */
  upcomingDays: number = UPCOMING_WINDOWS[2];
  /** The page: every live contract, or only the upcoming ones. */
  showAll = false;

  /** Whether the add/edit dialog is open (the header opens it, the body renders it). */
  formOpen = false;
  editingId: string | null = null;
  form: ContractForm = emptyContractForm();
  saving = false;
  formError: string | null = null;
  removingId: string | null = null;

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private store(path: string): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(path) as unknown as ServiceStoreType | null) ?? null;
  }

  private get service(): ServiceStoreType | null {
    return this.store("client-contracts");
  }

  private get clientsService(): ServiceStoreType | null {
    return this.store("clients");
  }

  /** Why the clients lookup failed (names fall back), or null. */
  clientsError: string | null = null;

  /** clientId (lower case) → name, for the cross-client page. */
  get clientNames(): Map<string, string> {
    const rows = (this.clientsService?.entities ?? []) as unknown as Array<{ id: string; name?: string; deletedAt?: string | null }>;
    return new Map(rows.map((c) => [String(c.id).toLowerCase(), c.name ?? "—"]));
  }

  clientName(clientId: string): string {
    return this.clientNames.get(String(clientId).toLowerCase()) ?? "Unknown client";
  }

  /** The clients a new contract can be for: live ones, by name. */
  get clientOptions(): Array<{ id: string; name: string }> {
    const rows = (this.clientsService?.entities ?? []) as unknown as Array<{ id: string; name?: string; deletedAt?: string | null }>;
    return rows
      .filter((c) => !c.deletedAt)
      .map((c) => ({ id: String(c.id), name: c.name ?? "—" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get isClientScope(): boolean {
    return this.scope !== ALL_CONTRACTS;
  }

  get today(): string {
    return todayLocal();
  }

  /** The scope's contracts, soonest renewal (or end) first; undated last. */
  get contracts(): ContractRecord[] {
    const all = ((this.service?.entities ?? []) as unknown as ContractRecord[]).filter((c) => c?.id != null);
    const scoped = this.isClientScope ? all.filter((c) => String(c.clientId).toLowerCase() === this.scope.toLowerCase()) : all;
    return scoped.slice().sort((a, b) => (a.keyDate ?? "9999").localeCompare(b.keyDate ?? "9999") || String(a.title ?? "").localeCompare(String(b.title ?? "")));
  }

  /** What the page lists: the upcoming ones, or every one. */
  get listed(): ContractRecord[] {
    return this.showAll ? this.contracts : this.contracts.filter((c) => isUpcoming(c, this.today, this.upcomingDays));
  }

  get upcomingCount(): number {
    return this.contracts.filter((c) => isUpcoming(c, this.today, this.upcomingDays)).length;
  }

  /** True when the read hit the cap: more contracts exist than are shown. */
  get capped(): boolean {
    return !this.isClientScope && this.contracts.length >= CONTRACTS_LIMIT;
  }

  /** The API sent values (it does only to admin and finance), so the form may carry them. */
  get valuesVisible(): boolean {
    return this.contracts.some((c) => c.valueHidden === false) || isAdmin(this.rootStore);
  }

  /** The web offers contract writes to administrators, as the billing pages do; the API decides. */
  get canEdit(): boolean {
    return isAdmin(this.rootStore);
  }

  setUpcomingDays(days: number) {
    this.upcomingDays = days;
  }

  setShowAll(value: boolean) {
    this.showAll = value;
  }

  async initialize(scope: string = ALL_CONTRACTS): Promise<void> {
    this.scope = scope || ALL_CONTRACTS;
    await Promise.all([this.load(), this.isClientScope ? Promise.resolve() : this.loadClients()]);
  }

  /** The names the cross-client page shows. A failure only means names fall back. */
  async loadClients(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.clientsError = null;
    try {
      await findOrThrow(this.clientsService, { query: { $limit: 500 } });
    } catch (err) {
      if (ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.clientsError = loadErrorMessage(err, "Client names could not be loaded.");
      });
    }
  }

  /** Re-read the scope's contracts. Never rejects: a failure lands on `error`. */
  async load(): Promise<void> {
    const scope = this.scope;
    const seq = ++this.seq;
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      const query: Record<string, unknown> = { $limit: CONTRACTS_LIMIT, $sort: { renewalDate: 1 } };
      if (scope !== ALL_CONTRACTS) query.clientId = scope;
      await findOrThrow(this.service, { query });
    } catch (err) {
      if (seq !== this.seq || ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Contracts could not be loaded.");
      });
    } finally {
      if (seq === this.seq && !ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  // ── the form ─────────────────────────────────────────────────────────────

  startCreate(clientId = this.isClientScope ? this.scope : "") {
    this.editingId = null;
    this.form = emptyContractForm(clientId);
    this.formError = null;
    this.formOpen = true;
  }

  startEdit(contract: ContractRecord) {
    this.editingId = contract.id;
    this.form = formFromContract(contract);
    this.formError = null;
    this.formOpen = true;
  }

  closeForm() {
    this.formOpen = false;
    this.formError = null;
  }

  setField<K extends keyof ContractForm>(field: K, value: ContractForm[K]) {
    this.form = { ...this.form, [field]: value };
    this.formError = null;
  }

  get formProblem(): string | null {
    return contractFormProblem(this.form);
  }

  private get api(): RawService | undefined {
    return this.rootStore.app as unknown as RawService | undefined;
  }

  /** Save the form. True when HQ took it; false with the API's reason on `formError`. */
  async save(): Promise<boolean> {
    const problem = this.formProblem;
    if (problem) {
      this.formError = problem;
      return false;
    }
    const ended = sessionMark(this.rootStore);
    this.saving = true;
    this.formError = null;
    try {
      const api = this.api;
      if (!api) throw new Error("Not connected to the API — reload and try again.");
      const body = contractBody(this.form, this.valuesVisible);
      if (this.editingId) await api.service("client-contracts").patch(this.editingId, body);
      else await api.service("client-contracts").create({ ...body, clientId: this.form.clientId });
      if (ended()) return false;
      runInAction(() => {
        this.editingId = null;
        this.form = emptyContractForm(this.isClientScope ? this.scope : "");
        this.formOpen = false;
      });
      await this.load();
      return true;
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.formError = loadErrorMessage(err, "The contract could not be saved.");
        });
      }
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.saving = false;
        });
      }
    }
  }

  /** Remove a contract (administrators; HQ keeps it, marked removed). */
  async remove(id: string): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.removingId = id;
    this.error = null;
    try {
      const api = this.api;
      if (!api) throw new Error("Not connected to the API — reload and try again.");
      await api.service("client-contracts").remove(id);
      if (ended()) return false;
      await this.load();
      return true;
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.error = loadErrorMessage(err, "The contract could not be removed.");
        });
      }
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.removingId = null;
        });
      }
    }
  }

  reset() {
    this.seq += 1;
    this.scope = ALL_CONTRACTS;
    this.loading = false;
    this.error = null;
    this.clientsError = null;
    this.upcomingDays = UPCOMING_WINDOWS[2];
    this.showAll = false;
    this.formOpen = false;
    this.editingId = null;
    this.form = emptyContractForm();
    this.saving = false;
    this.formError = null;
    this.removingId = null;
  }
}
